import { client } from '@/services/api/axios.ts';
import toast from 'react-hot-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { TPlant } from '@/features/plants/schemas/plants.schema.ts';
import { uploadImages } from '@/features/plants/utils/upload-images.ts';
import { plantKeys } from '@/features/plants/plant-query-keys.ts';

type CreatePlantResponse = {
  body: {
    plantId: string;
    message: string;
  };
};

type UseCreatePlantOptions = {
  onSuccess?: (data: TPlant) => void;
  onError?: (error: Error) => void;
};

export const useCreatePlant = ({ onSuccess, onError }: UseCreatePlantOptions = {}) => {
  const queryClient = useQueryClient();

  const createPlantWithImages = async (plant: TPlant): Promise<TPlant> => {
    const { data } = await client.post<CreatePlantResponse>('/plants', {
      basicDetails: plant.basicDetails,
      siteInformation: {
        ...plant.siteInformation,
        image: '',
      },
      technicalSpecifications: plant.technicalSpecifications,
      equipmentAndConnectionDetails: plant.equipmentAndConnectionDetails,
      financialInformation: plant.financialInformation,
      contactDetails: plant.contactDetails,
    });

    if (!data?.body?.plantId) {
      throw new Error('No plant ID returned from server.');
    }

    await uploadImages(plant.siteInformation.image, data.body.plantId);

    return {
      ...plant,
      plantId: data.body.plantId,
    };
  };

  return useMutation({
    mutationFn: (plant: TPlant) =>
      toast.promise(createPlantWithImages(plant), {
        loading: 'Creating plant...',
        success: 'Successfully created the plant.',
        error: (err: Error) => err.message || 'Failed to create plant.',
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: plantKeys.all });

      onSuccess?.(data);
    },
    onError: (error: Error) => {
      onError?.(error);
    },
  });
};
